import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  BellIcon,
  BookOpenIcon,
  FilmIcon,
  HandRaisedIcon,
  HomeIcon,
  MagnifyingGlassIcon,
  MegaphoneIcon,
  PencilSquareIcon,
  PlusIcon,
  UserCircleIcon,
  VideoCameraIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { onAuthStateChanged } from '@firebase/auth';
import { auth } from '../firebase';
import { listNotifications } from '../utils/secureFunctions';
import '../responsive.css';

type IconComponent = React.ComponentType<React.SVGProps<SVGSVGElement>>;

type NavAction = {
  key: string;
  label: string;
  icon: IconComponent;
  path?: string;
  isCreate?: boolean;
  badge?: number;
};

type CreateShortcut = {
  label: string;
  description: string;
  icon: IconComponent;
  path: string;
  accent: string;
};

const CREATE_SHORTCUTS: CreateShortcut[] = [
  { label: 'Post', description: 'Share a quick thought', icon: PencilSquareIcon, path: '/home', accent: 'var(--color-primary-500)' },
  { label: 'Pulse', description: 'Share the vibe', icon: FilmIcon, path: '/pulse', accent: '#F97316' },
  { label: 'Testimony', description: 'Boost someone\'s faith', icon: HandRaisedIcon, path: '/testimony', accent: '#0EA5E9' },
  { label: 'Story', description: 'Share short moments', icon: BookOpenIcon, path: '/stories', accent: '#A855F7' },
  { label: 'Go Live', description: 'Start a live moment', icon: VideoCameraIcon, path: '/go-live', accent: '#EF4444' },
  { label: 'Create Ad', description: 'Boost in seconds', icon: MegaphoneIcon, path: '/create-ad', accent: '#22C55E' },
];

export default function MobileBottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = React.useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= 768;
  });
  const [showCreateSheet, setShowCreateSheet] = React.useState(false);
  const [unreadCount, setUnreadCount] = React.useState(0);
  const [userId, setUserId] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  React.useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => setUserId(user?.uid ?? null));
    return unsubscribe;
  }, []);

  React.useEffect(() => {
    if (!userId) {
      setUnreadCount(0);
      return;
    }
    let mounted = true;
    const load = async () => {
      try {
        const res = await listNotifications({ unreadOnly: true, limit: 100 });
        if (mounted) setUnreadCount(res.notifications.length);
      } catch (err) {
        console.warn('Could not load notifications', err);
        if (mounted) setUnreadCount(0);
      }
    };
    load();
    const t = window.setInterval(load, 20000);
    return () => {
      mounted = false;
      window.clearInterval(t);
    };
  }, [userId]);

  React.useEffect(() => {
    if (!showCreateSheet || typeof document === 'undefined') return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [showCreateSheet]);

  const activeKey = React.useMemo(() => {
    const path = location.pathname.toLowerCase();
    if (path.startsWith('/search')) return 'search';
    if (path.startsWith('/notifications')) return 'notifications';
    if (path.startsWith('/profile') || /^\/[a-z0-9_]+$/.test(path)) return 'profile';
    return 'home';
  }, [location.pathname]);

  const navActions = React.useMemo<NavAction[]>(() => [
    { key: 'home', label: 'Home', icon: HomeIcon, path: '/home' },
    { key: 'search', label: 'Discover', icon: MagnifyingGlassIcon, path: '/search' },
    { key: 'create', label: 'Create', icon: PlusIcon, isCreate: true },
    { key: 'notifications', label: 'Alerts', icon: BellIcon, path: '/notifications', badge: unreadCount },
    { key: 'profile', label: 'Profile', icon: UserCircleIcon, path: '/profile' },
  ], [unreadCount]);

  const handleNav = (action: NavAction) => {
    if (action.isCreate) {
      setShowCreateSheet(true);
      return;
    }
    if (action.path) navigate(action.path);
  };

  if (!isMobile) return null;

  return (
    <>
      <nav className="mobile-bottom-nav" aria-label="Primary mobile navigation">
        <div className="mb-nav-inner" role="tablist" aria-label="Primary tabs">
          {navActions.map((action) => {
            const isActive = action.key === activeKey;
            const badgeValue =
              typeof action.badge === 'number' && action.badge > 0
                ? action.badge > 99
                  ? '99+'
                  : String(action.badge)
                : null;
            const classes = ['mb-nav-item'];
            if (isActive) classes.push('active');
            if (action.isCreate) classes.push('mb-nav-item--create');
            return (
              <button
                key={action.key}
                type="button"
                className={classes.join(' ')}
                onClick={() => handleNav(action)}
                aria-pressed={isActive}
                aria-label={action.label}
                aria-expanded={action.isCreate ? showCreateSheet : undefined}
              >
                <span className={action.isCreate ? 'mb-create-pill' : 'mb-nav-icon'} aria-hidden="true">
                  <action.icon />
                </span>
                <span className="mb-nav-label">{action.label}</span>
                {badgeValue && (
                  <span className="mb-nav-badge" aria-label={`${badgeValue} unread notifications`}>
                    {badgeValue}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </nav>

      {showCreateSheet && (
        <div
          className="mb-create-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Create on Belivah"
          onClick={() => setShowCreateSheet(false)}
        >
          <div className="mb-create-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="mb-create-header">
              <div>
                <p className="mb-create-title">Create something new</p>
                <p className="mb-create-subtitle">Share a story, moment, or promotion</p>
              </div>
              <button
                type="button"
                className="mb-create-close"
                onClick={() => setShowCreateSheet(false)}
                aria-label="Close create menu"
              >
                <XMarkIcon />
              </button>
            </div>
            <div className="mb-create-grid">
              {CREATE_SHORTCUTS.map((shortcut) => (
                <button
                  key={shortcut.label}
                  type="button"
                  className="mb-create-item"
                  onClick={() => {
                    setShowCreateSheet(false);
                    navigate(shortcut.path);
                  }}
                >
                  <span className="mb-create-item-icon" style={{ background: shortcut.accent }}>
                    <shortcut.icon />
                  </span>
                  <span className="mb-create-item-copy">
                    <span className="mb-create-item-label">{shortcut.label}</span>
                    <span className="mb-create-item-description">{shortcut.description}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
